/**
 * FaithDepthScreen — one question at a time.
 *
 * Sub-cards:
 *  0  servingImportance   (10-step scale)
 *  1  keepsCommandments   (yes/no)
 *  2  grewUpInChurch      (yes/no)
 *  3  churchAttendance    (frequency picker)
 *  4  spreadsGospel       (yes/no)
 *  5  hasIdols            (yes/no)
 *
 * Forward on tap — back available. Global Continue appears only on the
 * last sub-card when the screen's canContinue is true.
 */
import { useState } from "react";
import { Button } from "../components/Button";
import { QuestionCard } from "../components/QuestionCard";
import type { FaithPercent, Frequency } from "../domain/answers";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";

const FAITH_STEPS: FaithPercent[] = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const FREQUENCY_IDS: Exclude<Frequency, null>[] = ["frequent", "seldom", "never"];
const TOTAL = 6;

export function FaithDepthScreen() {
  const { profileAnswers, setProfileAnswers, next, canContinue } = useFlow();
  const t = useT();
  const f = profileAnswers.faith;

  const patch = (partial: Partial<typeof f>) =>
    setProfileAnswers((p) => ({ ...p, faith: { ...p.faith, ...partial } }));

  const [card, setCard] = useState(0);
  const pos = t("questionnaire.progressLabel", { current: String(card + 1), total: String(TOTAL) });

  const goNext = () => setCard((c) => Math.min(c + 1, TOTAL - 1));
  const goBack = () => setCard((c) => Math.max(c - 1, 0));

  /* ── Sub-card 0: Serving importance scale ── */
  if (card === 0) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard
          question={t("faithDepth.serving")}
          subtext={t("faithDepth.servingHint")}
          position={pos}
        >
          <div className="q-scale">
            {FAITH_STEPS.map((n) => (
              <button
                key={n}
                type="button"
                className={`q-scale__btn${f.servingImportance === n ? " is-selected" : ""}`}
                onClick={() => {
                  patch({ servingImportance: n });
                  goNext();
                }}
                aria-pressed={f.servingImportance === n}
              >
                {n}%
              </button>
            ))}
          </div>
          {card > 0 && (
            <button type="button" className="q-subnav__back" onClick={goBack}>
              ← {t("shell.back")}
            </button>
          )}
        </QuestionCard>
      </div>
    );
  }

  /* ── Sub-card 1: Keeps commandments ── */
  if (card === 1) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard question={t("faithDepth.keepsCommandments")} position={pos}>
          <div className="q-options">
            <button
              type="button"
              className={`q-option${f.keepsCommandments === true ? " is-selected" : ""}`}
              onClick={() => { patch({ keepsCommandments: true }); goNext(); }}
              aria-pressed={f.keepsCommandments === true}
            >
              <strong>{t("common.yes")}</strong>
            </button>
            <button
              type="button"
              className={`q-option${f.keepsCommandments === false ? " is-selected" : ""}`}
              onClick={() => { patch({ keepsCommandments: false }); goNext(); }}
              aria-pressed={f.keepsCommandments === false}
            >
              <strong>{t("common.no")}</strong>
            </button>
          </div>
          <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
            ← {t("shell.back")}
          </button>
        </QuestionCard>
      </div>
    );
  }

  /* ── Sub-card 2: Grew up in church ── */
  if (card === 2) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard question={t("faithDepth.grewUpInChurch")} position={pos}>
          <div className="q-options">
            <button
              type="button"
              className={`q-option${f.grewUpInChurch === true ? " is-selected" : ""}`}
              onClick={() => { patch({ grewUpInChurch: true }); goNext(); }}
              aria-pressed={f.grewUpInChurch === true}
            >
              <strong>{t("common.yes")}</strong>
            </button>
            <button
              type="button"
              className={`q-option${f.grewUpInChurch === false ? " is-selected" : ""}`}
              onClick={() => { patch({ grewUpInChurch: false }); goNext(); }}
              aria-pressed={f.grewUpInChurch === false}
            >
              <strong>{t("common.no")}</strong>
            </button>
          </div>
          <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
            ← {t("shell.back")}
          </button>
        </QuestionCard>
      </div>
    );
  }

  /* ── Sub-card 3: Church attendance (frequency) ── */
  if (card === 3) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard question={t("faithDepth.churchAttendance")} position={pos}>
          <div className="q-options">
            {FREQUENCY_IDS.map((id) => (
              <button
                key={id}
                type="button"
                className={`q-option${f.churchAttendance === id ? " is-selected" : ""}`}
                onClick={() => { patch({ churchAttendance: id }); goNext(); }}
                aria-pressed={f.churchAttendance === id}
              >
                <strong>{t(`faithDepth.${id}.label`)}</strong>
                <span>{t(`faithDepth.${id}.hint`)}</span>
              </button>
            ))}
          </div>
          <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
            ← {t("shell.back")}
          </button>
        </QuestionCard>
      </div>
    );
  }

  /* ── Sub-card 4: Spreads gospel ── */
  if (card === 4) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard question={t("faithDepth.spreadsGospel")} position={pos}>
          <div className="q-options">
            <button
              type="button"
              className={`q-option${f.spreadsGospel === true ? " is-selected" : ""}`}
              onClick={() => { patch({ spreadsGospel: true }); goNext(); }}
              aria-pressed={f.spreadsGospel === true}
            >
              <strong>{t("common.yes")}</strong>
            </button>
            <button
              type="button"
              className={`q-option${f.spreadsGospel === false ? " is-selected" : ""}`}
              onClick={() => { patch({ spreadsGospel: false }); goNext(); }}
              aria-pressed={f.spreadsGospel === false}
            >
              <strong>{t("common.no")}</strong>
            </button>
          </div>
          <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
            ← {t("shell.back")}
          </button>
        </QuestionCard>
      </div>
    );
  }

  /* ── Sub-card 5 (last): Has idols ── */
  return (
    <div className="screen-enter" style={{ flex: 1 }}>
      <QuestionCard question={t("faithDepth.hasIdols")} position={pos}>
        <div className="q-options">
          <button
            type="button"
            className={`q-option${f.hasIdols === true ? " is-selected" : ""}`}
            onClick={() => patch({ hasIdols: true })}
            aria-pressed={f.hasIdols === true}
          >
            <strong>{t("common.yes")}</strong>
          </button>
          <button
            type="button"
            className={`q-option${f.hasIdols === false ? " is-selected" : ""}`}
            onClick={() => patch({ hasIdols: false })}
            aria-pressed={f.hasIdols === false}
          >
            <strong>{t("common.no")}</strong>
          </button>
        </div>
        <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
          ← {t("shell.back")}
        </button>
      </QuestionCard>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={next} disabled={!canContinue}>
          {t("common.continue")}
        </Button>
      </div>
    </div>
  );
}
